import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDatabase } from '../contexts/DatabaseContext';
import { getAllForms } from '../forms';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiCopy, FiLayers, FiCheck, FiArrowRight } = FiIcons;

export const Templates = () => {
  const { db, isReady } = useDatabase();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const availableForms = getAllForms();

  useEffect(() => {
    if (isReady) {
      loadTemplates();
    }
  }, [isReady]);

  const loadTemplates = async () => {
    try {
      const templateList = await db.templates.orderBy('lastUsed').reverse().toArray();
      setTemplates(templateList || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await db.templates.delete(id);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const handleUseTemplate = async (template) => {
    await db.templates.update(template.id, { lastUsed: new Date() });
    navigate(`/forms/${template.formId}`, {
      state: { templateData: template.data }
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
              Certificate Templates & Presets
            </h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {templates.length} Presets
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Save time on repetitive inspections with pre-filled appliances, standard disclaimers, and engineer profiles.
          </p>
        </div>

        <button
          onClick={() => navigate('/forms')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold shadow-md transition-all"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>New Preset from Form</span>
        </button>
      </div>

      {/* Templates List Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <div className="inline-block animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mb-2"></div>
          <p className="text-xs">Loading templates...</p>
        </div>
      ) : templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => {
            const formConfig = availableForms.find((f) => f.id === template.formId);
            return (
              <div
                key={template.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm flex flex-col justify-between group hover:border-blue-500 transition-all"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="text-2xl">{formConfig?.icon || '📋'}</span>
                    {template.isDefault && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                        Default
                      </span>
                    )}
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    {template.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {formConfig?.name || template.formId}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-2">
                    Last used:{' '}
                    {template.lastUsed ? new Date(template.lastUsed).toLocaleDateString() : 'Never'}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    title="Delete Preset"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-all"
                  >
                    <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
                    <span>Apply Preset</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-12 text-center">
          <SafeIcon icon={FiLayers} className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">No Presets Created</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
            You can save any completed inspection as a reusable template to speed up recurring certificates.
          </p>
          <button
            onClick={() => navigate('/forms')}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold inline-flex items-center gap-2"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Browse Forms & Create</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Templates;
